import { useState, useRef } from 'react';
import { tie } from '../../utils/postScore';

const FIELD_KEYS = { pos1Score: 1, pos2Score: 2, pos1TieBrk: 3, pos2TieBrk: 4 };

export const getTabIndex = (setId, field) => (setId ? `${setId}${FIELD_KEYS[field]}` : String(FIELD_KEYS[field]));

const useScoreAutoAdvance = ({ enabled = true, sportTypeId }) => {
    const refs = useRef({});
    const pendingFocusRef = useRef(null);
    const [currentSet, setCurrentSet] = useState(null);

    const attachRef = (setId, field, node) => {
        if (!enabled) {
            return;
        }
        if (!refs.current[setId]) {
            refs.current[setId] = {};
        }
        refs.current[setId][field] = node;
        const pending = pendingFocusRef.current;
        if (node && pending && pending.setId === setId && pending.field === field) {
            node.focus();
            pendingFocusRef.current = null;
        }
    };

    const focusOrStage = (setId, field) => {
        const node = refs.current?.[setId]?.[field];
        if (node) {
            node.focus();
            pendingFocusRef.current = null;
            return;
        }
        pendingFocusRef.current = { setId, field };
    };

    const advanceToNextSet = (currentSetId, newSets) => {
        const nextSetExists = newSets.some(s => s.id === currentSetId + 1);
        if (nextSetExists) {
            setCurrentSet(currentSetId + 1);
            focusOrStage(currentSetId + 1, 'pos1Score');
        }
    };

    const advanceFrom = (set, key, newSets) => {
        if (key === 'pos1Score') {
            focusOrStage(set.id, 'pos2Score');
        } else if (key === 'pos2Score') {
            const setIndex = newSets.findIndex(s => s.id === set.id);
            if (tie(newSets[setIndex], sportTypeId)) {
                focusOrStage(set.id, 'pos1TieBrk');
            } else {
                advanceToNextSet(set.id, newSets);
            }
        } else if (key === 'pos1TieBrk') {
            focusOrStage(set.id, 'pos2TieBrk');
        } else if (key === 'pos2TieBrk') {
            advanceToNextSet(set.id, newSets);
        }
    };

    const onValueChange = ({ set, key, newSets, shouldAdvance }) => {
        if (!enabled || !shouldAdvance) {
            return;
        }
        if (key === 'pos1Score' || key === 'pos2Score') {
            advanceFrom(set, key, newSets);
        }
    };

    const onKeyDown = ({ set, key, event, newSets, shouldAdvance }) => {
        if (!enabled || !shouldAdvance) {
            return;
        }
        if (event.key !== 'Enter') {
            return;
        }
        event.preventDefault();
        advanceFrom(set, key, newSets);
    };

    return { attachRef, onValueChange, onKeyDown, currentSet, setCurrentSet };
};

export default useScoreAutoAdvance;
